/* 장소 사진 · 후기 미리보기 (/ds/place-review)
 *
 * 서버가 아직 없어 실제 화면에는 붙지 않았습니다. 붙었을 때 어떻게 보이는지
 * 여기서 확인합니다. 가짜 데이터는 이 파일에만 둡니다.
 */
import { useState } from 'react';
import { AppScreen, Section, SectionHeader, StarRating, PhotoViewerModal } from 'components/ds';
import { PlacePhotoSections } from 'components/place/PlacePhotoSections';
import type { StorePhoto } from 'components/place/StorePhotoSlots';
import { VisitPhotoSection } from 'components/place/VisitPhotos';
import type { VisitPhoto } from 'components/place/VisitPhotos';
import { VisitPhotoDetail } from 'components/place/VisitPhotoDetail';
import { VisitPhotoWrite } from 'components/place/VisitPhotoWrite';

const IMG = (seed: string) => `https://picsum.photos/seed/${seed}/600/600`;

const STORE: StorePhoto[] = [
    { photoId: 1, slot: 'EXTERIOR', imageUrl: IMG('front') },
    { photoId: 2, slot: 'INTERIOR', imageUrl: IMG('inside') },
];

const POSTS: VisitPhoto[] = [
    {
        postId: 1, userNo: 11, nickname: '지영', photos: [IMG('v1a'), IMG('v1b'), IMG('v1c')],
        rating: 5,
        content: '단체석이 넓어서 여덟 명이 앉아도 안 좁았어요. 주차는 건물 뒤편에 따로 있는데 입구에서는 안 보이니 미리 알고 가면 편합니다. 저녁 일곱 시쯤 갔는데 대기는 없었고, 주문한 지 십 분쯤 지나 다 나왔습니다. 다음 모임에도 여기로 하자는 얘기가 나왔을 만큼 다들 만족했어요.',
        tags: ['자리가 넉넉해요', '주차 편해요', '단체 모임'],
        createdAt: '2026-08-21', helpfulCount: 4, helpful: true, meetingName: '8월 정기모임',
    },
    {
        postId: 2, userNo: 12, nickname: '민수', photos: [IMG('v2a')], rating: 4,
        content: '맛은 좋았고 가격도 괜찮았습니다.',
        tags: ['맛있어요', '가성비 좋아요'], createdAt: '2026-08-14', helpfulCount: 1, mine: true,
    },
    ...[3, 4, 5, 6, 7, 8, 9, 10].map((n): VisitPhoto => ({
        postId: n, userNo: 20 + n, nickname: `방문자${n}`, photos: [IMG(`v${n}`)],
        rating: 4, createdAt: '2026-08-0' + (n % 9 + 1), helpfulCount: n % 3,
    })),
];

const TAGS = ['자리가 넉넉해요', '주차 편해요', '맛있어요', '가성비 좋아요', '조용해요'];

export const PlaceReviewPreview = () => {
    const [writing, setWriting] = useState(false);
    const [detail, setDetail] = useState<number | null>(null);
    const [storeViewer, setStoreViewer] = useState<string[] | null>(null);
    const [tag, setTag] = useState('');

    const filtered = tag ? POSTS.filter(p => p.tags?.includes(tag)) : POSTS;

    return (
        <AppScreen title="장소 사진 · 후기" bar="sub">
            <Section spacing="tight">
                <SectionHeader
                    title="화통삼 의왕점"
                    sub="내부 후기가 쌓이면 외부 평점 대신 이 별점이 표시됩니다"
                />
                <StarRating value={4.5} count={POSTS.length} source="CREW" />
            </Section>

            {/* 사진은 자리로 구분합니다 — 위는 가게, 아래는 다녀온 사람들 */}
            <PlacePhotoSections
                storePhotos={STORE}
                visitPosts={POSTS}
                visitTotal={POSTS.length}
                canAdd
                onOpenStore={(p) => setStoreViewer([p.imageUrl])}
                onAddVisit={() => setWriting(true)}
                onMoreVisit={() => undefined}
                onOpenVisit={(_p, i) => setDetail(i)}
            />

            {/* 전체 목록에서는 태그로 거릅니다 */}
            <Section divided last>
                <SectionHeader title="전체 목록 (태그 거르기)" />
                <VisitPhotoSection
                    posts={filtered}
                    total={filtered.length}
                    tags={TAGS}
                    activeTag={tag}
                    onChangeTag={setTag}
                    onOpen={(_p, i) => setDetail(i)}
                />
            </Section>

            <VisitPhotoDetail
                open={detail !== null}
                posts={POSTS}
                index={detail ?? 0}
                onIndexChange={setDetail}
                onClose={() => setDetail(null)}
            />
            <VisitPhotoWrite
                open={writing}
                onClose={() => setWriting(false)}
                placeName="화통삼 의왕점"
                onSubmit={() => setWriting(false)}
            />
            <PhotoViewerModal open={!!storeViewer} photos={storeViewer || []} onClose={() => setStoreViewer(null)} />
        </AppScreen>
    );
};
